import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import { PROBLEM_SHORT_LABELS } from "../../vocabulary";
import type { ProblemId } from "../../types";

const ALL_PROBLEMS = Object.keys(PROBLEM_SHORT_LABELS) as ProblemId[];

/**
 * The per-problem fix log: what was actually done (the operator logging it
 * is stamped automatically — never asked for).
 *
 * **Scoped by default (2026-08-13).** The button that opens this now lives on
 * the problem's own fix column, so the problem is already chosen and the
 * picklist is not rendered at all. Picking the problem you are already looking
 * at from a dropdown of sixteen was a chance to get it wrong for no benefit.
 * The unscoped form survives for the day a fix is logged outside a plan.
 *
 * **`Log fix` is the primary act since the re-cut.** Logging a fix RESOLVES
 * the problem it names, and when the last open problem on the plan resolves the
 * incident reaches fix-applied and the hotel moves to Monitoring.
 *
 * **There is ONE button (2026-08-14.)** A second one — "Log fix & run full
 * diagnosis" — used to sit beside it, asking the operator to choose between a
 * cheap check and the full five-layer run at the exact moment they had no
 * basis to choose: whether the change needs a browser to see it is a property
 * of the problem, not of how confident the person feels. The server now runs
 * the cheap corroboration pass behind every report either way, and the weekly
 * sweep answers everything that needs a browser. Where someone genuinely wants
 * the full run now, Diagnose now is still there, in Developer diagnostics.
 */
export function LogFixModal({
  problem: scoped,
  problemOptions,
  onSubmit,
  onClose,
  pending,
}: {
  /** Pre-chosen problem — the fix column's button passes this, and the
   *  picklist disappears. */
  problem?: { problemId: ProblemId; title: string };
  /** Unscoped form only: problems on the active plan lead the picklist, the
   *  full catalog follows for fixes outside a plan. */
  problemOptions?: Array<{ problemId: ProblemId; title: string }>;
  onSubmit: (body: { problem: ProblemId; note: string }) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [problem, setProblem] = useState<ProblemId>(
    scoped?.problemId ?? problemOptions?.[0]?.problemId ?? "1",
  );
  const [note, setNote] = useState("");
  const planIds = new Set((problemOptions ?? []).map((p) => p.problemId));
  const catalogRest = ALL_PROBLEMS.filter((id) => !planIds.has(id));

  return (
    <ActionModal
      title={scoped ? `Log fix — ${scoped.title}` : "Log fix"}
      submitLabel="Log fix"
      onSubmit={() => onSubmit({ problem, note: note.trim() })}
      onClose={onClose}
      pending={pending}
      disabled={!note.trim()}
    >
      {!scoped && (
        <div>
          <label className={FIELD_LABEL_CLASS}>Problem</label>
          <select
            className={INPUT_CLASS}
            value={problem}
            onChange={(e) => setProblem(e.target.value as ProblemId)}
          >
            {(problemOptions ?? []).map((p) => (
              <option key={p.problemId} value={p.problemId}>
                {p.title}
              </option>
            ))}
            {catalogRest.map((id) => (
              <option key={id} value={id}>
                {PROBLEM_SHORT_LABELS[id]}
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        {/* Never ask "who confirmed" — the operator logging this is stamped
            on the entry automatically. */}
        <label className={FIELD_LABEL_CLASS}>What was done</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[88px]`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Auto-link domains entry added in the vendor's dashboard"
          autoFocus
        />
      </div>
      <p className="text-xs text-gray-400">
        This marks the problem resolved on your word. Once every problem on the
        plan is resolved the hotel moves to Monitoring, its trusted data start
        date resets, and a real ad-driven booking is what proves the fix.
        <span className="block mt-1">
          A quick config check runs straight after, reading the live account —
          if it contradicts you, the incident reopens by itself and says so.
        </span>
      </p>
    </ActionModal>
  );
}
